import { format } from "date-fns";
import type { Member, Project } from "../../../graphql-client/codegen-types";
import type { AIPrompt } from "../../components/AIToolsBlock";
import { parseStartDate } from "../../components/gantt/utils";

const GENERATE_PROMPT = `Eres un experto en la generación del charter del proyecto a partir de los requerimientos y expectativas del cliente. A continuación te describo la solicitud del cliente, lo que se conoce en inglés como statement of work (SOW):

Requerimientos del producto o del servicio que se solicita:
Requerimientos del proyecto: [describe aquí los requerimientos del cliente]
Fecha de inicio: [fecha de inicio estimada]
Fecha de terminación (o duración): [fecha de terminación o duración estimada]
Cliente del proyecto: [nombre y cargo del cliente]
Usuarios del producto o servicio: [quiénes van a usar los productos o servicios que se entregarán al terminar el proyecto (entregables)]
Miembros del equipo del proyecto: [nombres del coordinador y de los integrantes]
Anexo como complemento el documento que describe lo que quiere el cliente o SOW, la propuesta, especificación o similar.

Con esta información desarrolla el Charter del proyecto para alimentarlo en la plataforma Coordinate. El charter que generes deberá tener los 10 elementos que se listan a continuación.

| # | Elemento del Charter | Ejemplo | Condición |
|---|---|---|---|
| 1 | Nombre del proyecto | Ventas Campaña 2020 | Título corto, representativo y atractivo. Máximo 50 caracteres. |
| 2 | Problemas, necesidades o requerimientos que dan origen al proyecto | No se tiene, no existe, hay demasiados, es limitado, es reducido... | Describe qué falta, qué funciona mal, qué carencia o exceso existe. Máximo 350 caracteres. |
| 3 | Objetivo del proyecto | Desarrollar un plan de capacitación para personal de mantenimiento | Verbo de acción + sujeto + complemento. Máximo 300 caracteres. |
| 4 | Entregables del proyecto | Diagnóstico, Prueba validada, Presentación expuesta a inversionistas | Lista lo que se va a entregar o presentar durante y al final del proyecto. |
| 5 | Beneficios esperados | Se generarán ahorros por $200,000 al mes. Se tendrá un dispositivo de más valor al cliente. | Lista los beneficios ordenados por importancia. |
| 6 | Cliente y usuarios | El cliente principal es el Ing. Arturo Fuentes director de Mercadotecnia. Los usuarios son los que van a comprar las nuevas máquinas. | Cliente es quien solicita el proyecto y acepta los resultados. Usuarios son los que van a usar los entregables. Máximo 350 caracteres. |
| 7 | Fechas estimadas | 21 de Mayo al 6 de Julio | Fecha de inicio y fecha de terminación del proyecto. |
| 8 | Equipo de trabajo | Adán López — coordinador del proyecto. Miembros del equipo Jorge Tellez y Rosalía Suárez | Nombres de los integrantes y su rol en el proyecto. |
| 9 | Suposiciones | Que el cliente tiene la información completa para el proyecto. | Eventos que se espera que ocurran para que avance el proyecto. |
| 10 | Restricciones | No contamos actualmente con un recurso especializado. | Limitaciones que, si no se superan, perjudican los objetivos del proyecto. |

El formato de salida es una lista con los 10 elementos solamente, no agregues nada más. Ejemplo:
1. Nombre del proyecto: Armado de poliedros
2. Problema o necesidad que da origen al proyecto: No se cuenta con un manual…
10. Restricciones: No contamos con…`;

const VERIFY_INTRO = `Eres un experto en gestión de proyectos. Evalúa el siguiente Charter, que corresponde a la definición de un proyecto en el formato ¿Qué y Por qué? de Coordinate.si.

Evalúa cada uno de los 10 criterios siguientes asignando una puntuación de 1 a 5, donde:
1 = No cumple con el criterio
2 = Cumple muy parcialmente
3 = Cumple a medias, con deficiencias importantes
4 = Cumple bien, con áreas menores de mejora
5 = Cumple completamente con el criterio

Criterios de evaluación:
1. Nombre del proyecto: ¿Es corto, memorable y atractivo? ¿Representa con precisión el alcance y propósito completo del proyecto?
2. Problema o necesidad: ¿Describe claramente una carencia, deficiencia o exceso que justifica el proyecto? ¿Está redactado como problema real y no como objetivo o solución?
3. Objetivo del proyecto: ¿Utiliza la estructura verbo de acción + sujeto + complemento? ¿Representa directamente la solución al problema descrito en el punto 2? ¿Es claro, específico y alcanzable?
4. Entregables: ¿Se listan usando sustantivos (no verbos)? ¿Incluye tanto entregables finales como intermedios? ¿Cada entregable es verificable mediante una prueba de logrado/no logrado? ¿Todos contribuyen significativamente al objetivo del proyecto?
5. Beneficios esperados: ¿Los beneficios están redactados desde la perspectiva del cliente? ¿Son concretos, medibles y denotan claramente el valor que aportará el proyecto?
6. Cliente y usuarios: ¿Se identifica con nombre y puesto al cliente (quien ordenó y recibirá el producto o servicio)? ¿Se distingue claramente el rol y nombre del usuario (quien lo utilizará)?
7. Fechas del proyecto: ¿Se especifica la fecha de inicio? ¿Se indica la duración estimada o la fecha de cierre del proyecto?
8. Equipo de trabajo: ¿Se lista con nombre y apellido a todas las personas que trabajarán en el proyecto?
9. Supuestos: ¿Se redactan como eventos o condiciones que se espera que ocurran para que el proyecto avance? ¿Son específicos y relevantes para el proyecto?
10. Restricciones: ¿Se describen los bloqueos, limitaciones o condiciones reales (no supuestos) que impiden o dificultan el avance del proyecto?

Formato de respuesta esperado. Para cada criterio presenta:
🔢 Puntuación: X/5
✅ Lo que está bien (si aplica)
🔧 Recomendación de mejora (obligatoria si la puntuación es 3 o menos; opcional si es 4)
💡 Ejemplo concreto de cómo reescribirlo (obligatorio si la puntuación es 2 o menos)

Al final presenta una tabla resumen con los 10 criterios y sus puntuaciones, el total obtenido sobre 50 puntos, y una recomendación general según esta escala:
10–30 puntos: 🔴 La definición del proyecto necesita mejorar significativamente
31–40 puntos: 🟡 La definición del proyecto puede mejorar
41–50 puntos: 🟢 La definición del proyecto es clara y está bien construida

La recomendación general debe señalar las 2 o 3 prioridades de mejora más importantes, redactadas de forma constructiva y accionable.`;

const EMPTY_VALUE = "(sin capturar)";

const formatText = (value?: string | null) => value?.trim() || EMPTY_VALUE;

const formatList = (items?: Array<{ text?: string | null }> | null) => {
  const texts = (items || [])
    .map(({ text }) => text?.trim())
    .filter((text): text is string => !!text);
  return texts.length
    ? texts.map((text, idx) => `\n${idx + 1}. ${text}`).join("")
    : EMPTY_VALUE;
};

const formatDate = (value?: string | null) =>
  value ? format(parseStartDate(value), "dd/MM/yyyy") : EMPTY_VALUE;

const getMemberName = ({ user, name }: Member) =>
  user ? [user.name, user.lastNames].filter(Boolean).join(" ") : name;

const buildVerifyPrompt = (project: Project) =>
  [
    VERIFY_INTRO,
    "--- MI CHARTER ---",
    `Nombre del proyecto: ${formatText(project.name)}`,
    `Problema u origen del proyecto: ${formatText(project.problems)}`,
    `Objetivo del proyecto: ${formatText(project.objective)}`,
    `Entregables: ${formatList(project.wbsRoot?.children)}`,
    `Cliente y usuarios: ${formatText(project.client)}`,
    `Suposiciones: ${formatList(project.assumptions)}`,
    `Restricciones: ${formatList(project.restrictions)}`,
    `Beneficios esperados: ${formatList(project.benefits)}`,
    `Fechas estimadas: inicio ${formatDate(
      project.startEstimate
    )}, fin ${formatDate(project.endEstimate)}`,
    `Equipo de trabajo: ${formatList(
      project.members?.map((member) => ({ text: getMemberName(member) }))
    )}`,
  ].join("\n\n");

export const getGenerateCharterPrompt = (): AIPrompt => ({
  buttonText: "Generar mi Charter",
  tooltip:
    "Te presenta un texto de prompt para que lo alimentes a una IA y te genere el draft del charter y luego lo alimentes en este paso",
  modalTitle: "Generar mi Charter",
  steps: [
    "Da clic en Copiar texto completo.",
    "Abre ChatGPT, Claude u otra IA en una nueva pestaña.",
    "Completa los datos entre corchetes, pega el texto en la IA y anexa tu SOW si lo tienes.",
    "Copia el resultado generado y pégalo en los campos de Coordinate.",
  ],
  getText: () => GENERATE_PROMPT,
});

export const getVerifyCharterPrompt = (project: Project): AIPrompt => ({
  buttonText: "Verificar mi Charter",
  tooltip:
    "Te presenta un texto de prompt y el texto de tu charter para que lo alimentes en una IA y te sugiera mejoras al Charter",
  modalTitle: "Verificar mi Charter",
  steps: [
    "Da clic en Copiar texto completo.",
    "Abre ChatGPT, Claude u otra IA en una nueva pestaña.",
    "Pega el texto copiado en el chat de tu IA favorita.",
    "Lee la retroalimentación y regresa a Coordinate para aplicar las mejoras.",
  ],
  getText: () => buildVerifyPrompt(project),
});
